import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canRead, type Role, type SectionAccess } from '@/lib/permissions';
import type { Database, Tables } from '@/types/database';

const KEY = ['profiles'];

export function useProfiles() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('full_name', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data as Tables<'profiles'>[];
    },
  });
}

export function useUpdateProfileRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: Role }) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({ role })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateProfileSectionAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      section_access,
    }: {
      id: string;
      section_access: SectionAccess;
    }) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({ section_access })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateProfileActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({ is_active })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

const INVITES_KEY = ['user-invites'];

export type UserInvite = Tables<'user_invites'>;

export function usePendingInvites() {
  return useQuery({
    queryKey: INVITES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_invites')
        .select('*')
        .is('accepted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as UserInvite[];
    },
  });
}

export interface SendInviteInput {
  email: string;
  role: Role;
  full_name?: string;
  note?: string;
}

/** Creates (or updates) an invite and emails it. Pass `invite_id` to re-send. */
export function useSendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendInviteInput | { invite_id: string }) => {
      const { data, error } = await supabase.functions.invoke('user-invite', { body: input });
      if (error) throw new Error(await extractFunctionErrorMessage(error));
      return data as { ok: true; invite_id: string };
    },
    // The invite row is saved even when the email fails, so refresh either way.
    onSettled: () => qc.invalidateQueries({ queryKey: INVITES_KEY }),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('user_invites').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: INVITES_KEY }),
  });
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as FunctionsHttpError).context as Response | undefined;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}

export type AuditLog = Omit<Tables<'audit_logs'>, 'before' | 'after'> & {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
};

export type AuditSortColumn = 'created_at' | 'action' | 'entity_type' | 'actor_name';
export type AuditSort = { column: AuditSortColumn; dir: 'asc' | 'desc' };

/** Sentinel for "rows with no actor" (service-role / sync / trigger writes). */
export const AUDIT_SYSTEM_ACTOR = 'system';

export interface AuditLogQuery {
  /** Free text: matches actor name/email, entity label/id/type, action, IP. */
  search?: string;
  entityType?: string;
  /** Exact `entity_id` — the full history of one record. */
  entityId?: string;
  action?: string;
  /** A profile id, or `AUDIT_SYSTEM_ACTOR` for automated writes. */
  actorId?: string;
  /** Local calendar dates `YYYY-MM-DD`, inclusive. */
  from?: string;
  to?: string;
  sort?: AuditSort;
  /** 0-based. */
  page?: number;
  pageSize?: number;
}

export const AUDIT_DEFAULT_PAGE_SIZE = 50;
export const AUDIT_EXPORT_MAX_ROWS = 5000;

type AuditLogSearchArgs = Database['public']['Functions']['audit_log_search']['Args'];
type AuditLogSearchResult = Database['public']['Functions']['audit_log_search']['Returns'];

function auditSearchArgs(query: AuditLogQuery): AuditLogSearchArgs {
  const isSystem = query.actorId === AUDIT_SYSTEM_ACTOR;
  return {
    p_search: query.search?.trim() || undefined,
    p_entity_type: query.entityType || undefined,
    p_entity_id: query.entityId || undefined,
    p_action: query.action || undefined,
    p_actor_id: isSystem ? undefined : query.actorId || undefined,
    p_system: isSystem || undefined,
    p_from: query.from ? new Date(`${query.from}T00:00:00`).toISOString() : undefined,
    p_to: query.to ? new Date(`${query.to}T23:59:59.999`).toISOString() : undefined,
    p_sort: query.sort?.column ?? 'created_at',
    p_ascending: (query.sort?.dir ?? 'desc') === 'asc',
  };
}

/**
 * Audit log rows include `before` / `after` JSON snapshots of every audited
 * row, so they can leak financial data (bank balances, transaction amounts,
 * Mercury / QBO integration metadata). Restricted to `super_admin`; the DB
 * enforces the same via RLS in migration 24.
 *
 * Filtering, search, sorting, and paging all run in Postgres
 * (`audit_log_search`, migration 139) so the page stays inside the statement
 * timeout however large the trail grows.
 */
export function useAuditLogSearch(query: AuditLogQuery) {
  const { role, loading } = useAuth();
  const enabled = !loading && canRead(role, 'audit');
  const page = query.page ?? 0;
  const pageSize = query.pageSize ?? AUDIT_DEFAULT_PAGE_SIZE;
  return useQuery({
    queryKey: ['audit-logs', 'search', query],
    enabled,
    // Keep the current rows on screen while the next page / filter loads.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('audit_log_search', {
        ...auditSearchArgs(query),
        p_limit: pageSize,
        p_offset: page * pageSize,
      });
      if (error) throw error;
      const result = data as AuditLogSearchResult;
      return { rows: result.rows as unknown as AuditLog[], total: result.total ?? 0 };
    },
  });
}

export type AuditLogFacets = Database['public']['Functions']['audit_log_facets']['Returns'];

export function useAuditLogFacets() {
  const { role, loading } = useAuth();
  const enabled = !loading && canRead(role, 'audit');
  return useQuery({
    queryKey: ['audit-logs', 'facets'],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('audit_log_facets');
      if (error) throw error;
      return data as AuditLogFacets;
    },
  });
}

export type AuditActorSummary = Database['public']['Functions']['audit_actor_summary']['Returns'];

export function useAuditActorSummary(actorId: string | null) {
  const { role, loading } = useAuth();
  const enabled = !loading && canRead(role, 'audit') && !!actorId;
  return useQuery({
    queryKey: ['audit-logs', 'actor-summary', actorId],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('audit_actor_summary', { p_actor_id: actorId! });
      if (error) throw error;
      return data as AuditActorSummary;
    },
  });
}

/** Same filters / sort as the table, without paging, for CSV export. */
export async function fetchAuditLogsForExport(
  query: AuditLogQuery,
  max = AUDIT_EXPORT_MAX_ROWS,
): Promise<AuditLog[]> {
  const { data, error } = await supabase.rpc('audit_log_search', {
    ...auditSearchArgs(query),
    p_limit: max,
    p_with_total: false,
  });
  if (error) throw error;
  return (data as AuditLogSearchResult).rows as unknown as AuditLog[];
}
